import { BehaviorSubject, map, Observable, of, share, tap } from "rxjs";
import { environment } from "src/environments/environment";

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { OnboardingItem } from "../models/onboarding-item.model";

@Injectable({
  providedIn: "root",
})
export class OnboardingService {
  url = environment.apiUrl + "/onboarding";

  private currentOnboardingItemSubject = new BehaviorSubject<string | null>(
    localStorage.getItem("currentOnboardingItem"),
  );
  private currentOnboardingItemStepSubject = new BehaviorSubject<string | null>(
    localStorage.getItem("currentOnboardingItemStep"),
  );

  // Observable streams
  currentOnboardingItem$ = this.currentOnboardingItemSubject.asObservable();
  currentOnboardingItemStep$ = this.currentOnboardingItemStepSubject.asObservable();

  // Cache pour les données d'onboarding
  private onboardingItemsCache: OnboardingItem[] | null = null;
  private onboardingItemsSubject = new BehaviorSubject<OnboardingItem[]>([]);
  onboardingItems$ = this.onboardingItemsSubject.asObservable();

  // Variable pour suivre l'appel en cours
  private ongoingRequest: Observable<OnboardingItem[]> | null = null;

  constructor(private http: HttpClient) {
    // Écouter les changements dans le localStorage
    window.addEventListener("storage", (event) => {
      if (event.key === "currentOnboardingItem") {
        this.currentOnboardingItemSubject.next(event.newValue);
      }
      if (event.key === "currentOnboardingItemStep") {
        this.currentOnboardingItemStepSubject.next(event.newValue);
      }
    });
  }

  // Méthode pour récupérer le statut d'onboarding depuis l'API ou le cache
  getOnboardingStatus(forceReload: boolean = false): Observable<OnboardingItem[]> {
    // Si nous avons déjà les données en cache, les retourner
    if (this.onboardingItemsCache && !forceReload) {
      return of(this.onboardingItemsCache);
    }

    // Si un appel est déjà en cours, retourner cet appel
    if (this.ongoingRequest) {
      return this.ongoingRequest;
    }

    // Sinon, appeler l'API et mettre en cache les résultats
    this.ongoingRequest = this.http.get<OnboardingItem[]>(this.url).pipe(
      map((res: any) => res.items),
      tap((items) => {
        this.onboardingItemsCache = items;
        this.onboardingItemsSubject.next(items);
        this.ongoingRequest = null; // Réinitialiser l'appel en cours
      }),
      share(), // Partager la même réponse entre plusieurs abonnés
    );

    return this.ongoingRequest;
  }

  // Méthode pour forcer le rafraîchissement des données du cache
  refreshOnboardingStatus(): Observable<OnboardingItem[]> {
    // Vider le cache
    this.onboardingItemsCache = null;

    // Appeler l'API et mettre à jour le cache
    return this.http.get<OnboardingItem[]>(this.url).pipe(
      map((res: any) => res.items),
      tap((items) => {
        this.onboardingItemsCache = items;
        this.onboardingItemsSubject.next(items);
      }),
    );
  }

  // Méthode pour mettre à jour uniquement le cache local sans appel API
  updateLocalItemStatus(itemId: string, done: boolean): void {
    // Mettre à jour le cache si nous l'avons
    if (this.onboardingItemsCache) {
      const itemIndex = this.onboardingItemsCache.findIndex((item) => item.id === itemId);
      if (itemIndex !== -1) {
        this.onboardingItemsCache[itemIndex].done = done;
        this.onboardingItemsSubject.next([...this.onboardingItemsCache]);
      }
    }
  }

  // Méthodes pour gérer l'état de l'onboarding
  setCurrentOnboardingItem(itemId: string | null) {
    if (itemId) {
      localStorage.setItem("currentOnboardingItem", itemId);
    } else {
      localStorage.removeItem("currentOnboardingItem");
    }
    this.currentOnboardingItemSubject.next(itemId);
  }

  setCurrentOnboardingStep(step: string | null) {
    if (step) {
      localStorage.setItem("currentOnboardingItemStep", step);
    } else {
      localStorage.removeItem("currentOnboardingItemStep");
    }
    this.currentOnboardingItemStepSubject.next(step);
  }

  // Méthode pour réinitialiser complètement l'onboarding
  resetOnboarding() {
    localStorage.removeItem("currentOnboardingItem");
    localStorage.removeItem("currentOnboardingItemStep");
    this.currentOnboardingItemSubject.next(null);
    this.currentOnboardingItemStepSubject.next(null);
  }

  submitFeedback(payload: { note: number; comment: string }) {
    return this.http.post(`${this.url}/feedback`, payload);
  }

  getLastFeedback() {
    return this.http.get<{ note?: number; comment?: string; date?: string }>(`${this.url}/last-feedback`);
  }
}
